import { Component, computed, effect, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { GenericHeaderComponent } from '@components/generic-header/generic-header.component';

import { CommonModule } from '@angular/common';
import { SalesService } from 'app/services/sales.service';
import { Router } from '@angular/router';
import { catchError, debounceTime, filter, of, pipe, Subject, switchMap, takeUntil } from 'rxjs';
import { Sale } from 'app/interfaces/sale.interface';
import { SalesListComponent } from '@components/my-sales/sales-list/sales-list.component';
import { ButtonComponent } from '@components/button/button.component';
import { ApiService, UserInfo } from 'app/services/api.service'
import { AnalyticsService } from 'app/services/analytics.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-mysales',
    templateUrl: './mysales.component.html',
    styleUrl: './mysales.component.scss',
    standalone: true,
    imports: [TranslateModule, GenericHeaderComponent, CommonModule, SalesListComponent, ButtonComponent],
})
export class MysalesComponent implements OnInit, OnDestroy {
    public sales!: Signal<Sale[]>
    public hasNextPage!: Signal<boolean>
    public status: WritableSignal<string> = signal<string>('all')
    public page: WritableSignal<number> = signal<number>(0)
    public query: WritableSignal<string> = signal<string>('')
    public userInfo!: WritableSignal<UserInfo>
    public isFabricator = computed(() => this.userInfo().accountType === 'FABRICATOR')

    public currentSales = signal<number>(0)
    public nextLevelSales = signal<number>(0)
    public currentLevelSales = signal<number>(0)
    public currentStatus: string = ''
    public registerSaleDescription = computed(() => {
        if(this.currentStatus && this.currentStatus.toLowerCase() === 'elite'){
            return this.translateService
                .instant('my_sales.register_sale_description_elite')
                .replace('XXXX', this.currentLevelSales())
                .replace('YYYY', this.currentStatus)
                .replace('ZZZZ', this.nextLevelSales())
        } else if(this.currentStatus && this.currentStatus.toLowerCase() === 'advanced') {
            return this.translateService
                .instant('my_sales.register_sale_description_advanced')
                .replace('XXXX', this.nextLevelSales())
        } else {
            return this.translateService
                .instant('my_sales.register_sale_description_premium')
                .replace('XXXX', this.currentLevelSales())
                .replace('YYYY', this.currentStatus)
        }
    })
    public availableActios: number = 0
    public validatingActios: number = 0

    // Nuevos objetivos para 2026
    private readonly ELITE_SALES_TARGET = 12
    private readonly PREMIUM_SALES_TARGET = 24

    // Computed para lógica 2026
    public hasReachedCurrentLevelTarget = computed(() => {
        if (this.currentStatus === 'ELITE') {
            return this.currentSales() >= this.ELITE_SALES_TARGET
        } else if (this.currentStatus === 'PREMIUM') {
            return this.currentSales() >= this.PREMIUM_SALES_TARGET
        }
        return false
    })

    public title2026 = computed(() => {
        if (this.isFabricator()) {
            return 'my_sales.register_new_sale_label_fabricator'
        }
        return this.hasReachedCurrentLevelTarget()
            ? 'my_sales.register_sales_increase_level'
            : 'my_sales.register_sales_maintain_level'
    })

    public description2026 = computed(() => {
        const current = this.currentSales()
        let target: number
        let level: string

        if (this.hasReachedCurrentLevelTarget()) {
            // Ya alcanzó el objetivo actual, mostrar para subir de nivel
            if (this.currentStatus === 'ELITE') {
                target = this.PREMIUM_SALES_TARGET
                level = 'PREMIUM'
            } else {
                // Ya es PREMIUM, mantener mensaje de mantener
                target = this.PREMIUM_SALES_TARGET
                level = 'PREMIUM'
            }
        } else {
            // No ha alcanzado el objetivo actual, mostrar para mantener
            if (this.currentStatus === 'ELITE') {
                target = this.ELITE_SALES_TARGET
                level = 'ELITE'
            } else if (this.currentStatus === 'PREMIUM') {
                target = this.PREMIUM_SALES_TARGET
                level = 'PREMIUM'
            } else {
                target = this.ELITE_SALES_TARGET
                level = 'ELITE'
            }
        }

        const remaining = Math.max(0, target - current)
        const key = this.hasReachedCurrentLevelTarget() ? 'my_sales.register_sales_to_increase' : 'my_sales.register_sales_to_maintain'

        return this.translateService
            .instant(key)
            .replace('{count}', remaining.toString())
            .replace('{level}', level)
    })

    public salesTarget2026 = computed(() => {
        if (this.hasReachedCurrentLevelTarget()) {
            // Mostrar objetivo del siguiente nivel
            if (this.currentStatus === 'ELITE') {
                return this.PREMIUM_SALES_TARGET
            } else {
                return this.PREMIUM_SALES_TARGET
            }
        } else {
            // Mostrar objetivo del nivel actual
            if (this.currentStatus === 'ELITE') {
                return this.ELITE_SALES_TARGET
            } else if (this.currentStatus === 'PREMIUM') {
                return this.PREMIUM_SALES_TARGET
            }
            return this.ELITE_SALES_TARGET
        }
    })

    private filters$ = new Subject<{ status: string; page: number; query: string }>()
    private destroy$ = new Subject<void>()
    salesLoaded = false

    public isLoyalty2026Active: boolean = environment.loyalty2026

    private searchTerm$ = new Subject<string>();

    constructor(private salesService: SalesService, private apiService: ApiService, private translateService: TranslateService, private router: Router, private analytics: AnalyticsService) {
        this.sales = this.salesService.sales
        this.hasNextPage = this.salesService.hasNextPage
        this.userInfo = signal(this.apiService.userInfo.getValue())

        // Efecto reactivo para cargar la lista de ventas al cambiar el estado, página o consulta
        effect(
            () => {
                this.filters$.next({
                    status: this.status(),
                    page: this.page(),
                    query: this.query(),
                })
            },
            {
                allowSignalWrites: true, // Permite que las señales se escriban dentro del efecto
            }
        )
    }

    private translations(){
        this.translateService.get([
            'my_sales.register_sales_increase_level',
            'my_sales.register_sales_maintain_level',
            'my_sales.register_sales_to_increase',
            'my_sales.register_sales_to_maintain'
        ])
    }

    public ngOnInit(): void {
        this.userInfo.set(this.apiService.userInfo.getValue())
        const userInfo = localStorage.getItem('weUserInfo')
        if (userInfo) {
            const { ytdSales, loyaltyLevel, country, availableActios, validatingActios } = JSON.parse(userInfo)
            this.currentSales.set(ytdSales)
            this.currentStatus = loyaltyLevel
            this.availableActios = availableActios
            this.validatingActios = validatingActios

            this.apiService.getNextLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
                next: (nextLevel: any) => {
                    this.nextLevelSales.set(nextLevel.ytdSales ?? 0)
                },
                error: (error: any) => console.log(error),
            })

            this.apiService.getCurrentLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
                next: (currentLevel: any) => this.currentLevelSales.set(currentLevel.ytdSales ?? 0),
                error: (error: any) => console.log('Error al pedir los requisitos del nivel actual:', error),
            })
        }

        this.filters$
            .pipe(
                debounceTime(500),
                switchMap((filters) =>
                    this.salesService.loadSalesList(filters.status, filters.page, 10, 'id,desc', filters.query).pipe(
                        catchError((error) => {
                            console.error('Error loading sales:', error)
                            return of([]) // Retorna array vacío en caso de error para continuar el stream
                        })
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (sales: Sale[]) => {
                    console.log('Sales loaded:', sales)
                    this.salesLoaded = true
                },
            })

        this.searchTerm$
            .pipe(debounceTime(800)) // ajusta el tiempo según lo que consideres "pausa"
            .subscribe((term) => {
                this.analytics.trackCustom('search', {
                search_term: term,
                type: 'sales-registered'
                });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    public navigateToRegisterSale(): void {
        this.router.navigate(['new-sales'])
    }

    public handleSearch(term: string): void {
        this.query.set(term);
        this.searchTerm$.next(term);
    }


    public loadNextPage(): void {
        if (this.hasNextPage()) {
            this.page.update((page: number) => page + 1)
        }
    }
    public handleDeleteSale(_saleId?: string): void {
        // 1) Si estás en otra página, vuelve a la primera: el effect() disparará filters$.next(...)
        if (this.page() !== 0) {
            this.page.set(0)
            return
        }

        // 2) Ya estás en la primera página: fuerza la recarga reenviando los filtros actuales
        this.filters$.next({
            status: this.status(),
            page: this.page(),
            query: this.query(),
        })
    }
}
